import React, { useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import {
  MapContainer,
  TileLayer,
  Circle,
  Polygon,
  Marker as LeafletMarker,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ReactComponent as Marker } from "./gps.svg";
import Button from "@material-ui/core/Button";
import * as turf from "@turf/turf";
import { ReactComponent as Locator } from "./location.svg";
import MapSearchInput from "./MapSearchInput";
import { ReactComponent as LocatorPin } from "../../AreaSelect/AreasSelect/locator.svg";
import { distance, getAllowedDistance } from "./helpers";

import "./mapSearchStyles.css";
import instance from "../../../store/actions/instance";

function MapPicker({
  language,
  defaultPosition,
  savePosition,
  countryObj,
  selectedAreaObj,
  themeColor,
  settings,
}) {
  const [map, setMap] = useState(null);
  const [position, setPosition] = useState(
    defaultPosition || { lat: 29.38, lng: 47.99 },
  );

  /* ---------------- Search select ---------------- */

  const countryCodes = useMemo(() => {
    return countryObj?.alpha_2_code
      ? [countryObj.alpha_2_code.toLowerCase()]
      : ["kw", "eg", "sa", "bh", "om", "qa"];
  }, [countryObj?.alpha_2_code]);

  const handleSearchSelect = ({ lat, lng }) => {
    setPosition({ lat, lng });
    if (map) {
      map.flyTo([lat, lng], 17);
    }
  };

  /* ---------------- Reverse geocode ---------------- */

  const reverseGeocode = async (lat, lng) => {
    if (!settings.enable_reverse_geocode) return {};
    const res = await instance.post("/map_reverse_geo/", {
      lat,
      lng,
      language: language === "arabic" ? "ar" : "en",
    });
    return res.data;
  };

  const handleSave = async () => {
    let geo = null;

    try {
      geo = await reverseGeocode(position.lat, position.lng);
    } catch (e) {
      console.error("Reverse geocode failed", e);
    }

    const addr = geo?.address || {};

    savePosition({
      lat: position.lat,
      lng: position.lng,
      gpsConfirmed: true,

      // canonical mapping
      street: addr.road || addr.amenity || addr.shop || addr.tourism || "",
      building: addr.house_number || "",
      block: addr.neighbourhood || "",

      // metadata
      display_name: geo?.display_name || "",
      geocode_provider: "nominatim",
      geocode_confidence: addr.house_number ? "exact" : "approximate",
    });
  };

  /* ---------------- Map movement ---------------- */

  const onMove = useCallback(() => {
    if (!map) return;
    setPosition(map.getCenter());
  }, [map]);

  useEffect(() => {
    if (!map) return;
    map.on("move", onMove);
    return () => map.off("move", onMove);
  }, [map, onMove]);

  const getCurrentPosition = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (res) => {
        const lat = res.coords.latitude;
        const lng = res.coords.longitude;
        setPosition({ lat, lng });
        if (map) {
          map.flyTo([lat, lng], 17);
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 6000 },
    );
  };

  /* ---------------- Area validation ---------------- */

  const basePosition = useMemo(() => {
    if (!selectedAreaObj) return null;
    if (selectedAreaObj.lat && selectedAreaObj.lng)
      return {
        lat: parseFloat(selectedAreaObj.lat),
        lng: parseFloat(selectedAreaObj.lng),
      };
    if (selectedAreaObj.area_lat && selectedAreaObj.area_lng)
      return {
        lat: parseFloat(selectedAreaObj.area_lat),
        lng: parseFloat(selectedAreaObj.area_lng),
      };
    return null;
  }, [selectedAreaObj]);

  const distanceFromBase = useMemo(() => {
    if (!position || !basePosition) return null;
    return distance(
      position.lat,
      position.lng,
      basePosition.lat,
      basePosition.lng,
    );
  }, [position, basePosition]);

  const inside = useMemo(() => {
    if (selectedAreaObj?.shapes?.length) {
      const pt = turf.point([position.lng, position.lat]);

      return selectedAreaObj.shapes.some((shape) => {
        if (shape.type === "Polygon" || shape.type === "Rectangle") {
          const coords = shape.points.map((p) => [p.lng, p.lat]);
          if (
            coords[0][0] !== coords.at(-1)[0] ||
            coords[0][1] !== coords.at(-1)[1]
          ) {
            coords.push(coords[0]);
          }
          return turf.booleanPointInPolygon(pt, turf.polygon([coords]));
        }

        if (shape.type === "Circle") {
          const center = turf.point([shape.center.lng, shape.center.lat]);
          return turf.distance(pt, center, { units: "meters" }) <= shape.radius;
        }

        if (shape.type === "Marker") {
          const marker = turf.point([shape.point.lng, shape.point.lat]);
          return turf.distance(pt, marker, { units: "meters" }) <= 10;
        }

        return false;
      });
    }

    if (!settings.enable_map_pin_selection && distanceFromBase !== null) {
      return distanceFromBase <= getAllowedDistance(settings);
    }

    return true;
  }, [
    position,
    selectedAreaObj,
    settings.enable_map_pin_selection,
    distanceFromBase,
  ]);

  /* ---------------- Render ---------------- */

  return (
    <div style={{ position: "relative" }}>
      {/* 🔍 Standalone OSM autocomplete */}
      {settings.enable_map_search && (
        <MapSearchInput
          language={language}
          countryCodes={countryCodes}
          onSelect={handleSearchSelect}
        />
      )}

      <MapContainer
        center={[position.lat, position.lng]}
        zoom={15}
        scrollWheelZoom
        whenCreated={setMap}
        style={{ height: "calc(100vh - 117px)" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {selectedAreaObj?.shapes?.map((shape, idx) => {
          if (shape.type === "Polygon" || shape.type === "Rectangle")
            return (
              <Polygon
                key={idx}
                positions={shape.points.map((p) => [p.lat, p.lng])}
                pathOptions={{ color: themeColor, fillOpacity: 0.1 }}
              />
            );

          if (shape.type === "Circle")
            return (
              <Circle
                key={idx}
                center={[shape.center.lat, shape.center.lng]}
                radius={shape.radius}
                pathOptions={{ color: themeColor, fillOpacity: 0.1 }}
              />
            );

          if (shape.type === "Marker")
            return (
              <LeafletMarker
                key={idx}
                position={[shape.point.lat, shape.point.lng]}
              />
            );

          return null;
        })}

        {!selectedAreaObj?.shapes?.length &&
          !settings.enable_map_pin_selection &&
          basePosition && (
            <Circle
              center={[basePosition.lat, basePosition.lng]}
              pathOptions={{ color: themeColor, fillOpacity: 0.05 }}
              radius={getAllowedDistance(settings) * 1000}
            />
          )}

        {/* Center pin */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-100%)",
            width: 24,
            height: 36,
            pointerEvents: "none",
            zIndex: 500,
          }}
        >
          <LocatorPin
            style={{
              width: 40,
              height: 40,
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              fill: "#606060",
              margin: "auto",
            }}
          />
        </div>
      </MapContainer>

      {/* 📍 Locate me */}
      <div
        onClick={getCurrentPosition}
        style={{
          width: 50,
          height: 50,
          position: "absolute",
          right: 10,
          top: 10,
          background: "white",
          borderRadius: 2,
          cursor: "pointer",
          zIndex: 401,
          border: "2px solid rgba(0,0,0,0.2)",
        }}
      >
        <Locator
          style={{
            width: 25,
            height: 25,
            position: "absolute",
            inset: 0,
            margin: "auto",
            fill: "#606060",
          }}
        />
      </div>

      {/* 💾 Save */}
      <div
        className={
          language === "english"
            ? "action-button-english"
            : "action-button-arabic"
        }
        style={{ background: "white", height: 60, zIndex: 401 }}
      >
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disabled={!inside}
          style={{ height: "100%", boxShadow: "none" }}
          onClick={handleSave}
        >
          {!inside
            ? language === "arabic"
              ? !settings.enable_map_pin_selection &&
                !selectedAreaObj?.shapes?.length
                ? `العنوان بعيد جدا عن ${selectedAreaObj?.ar_name || "المنطقة"}`
                : `العنوان غير مسموح في ${
                    selectedAreaObj?.ar_name || "المنطقة"
                  }`
              : !settings.enable_map_pin_selection &&
                  !selectedAreaObj?.shapes?.length
                ? `Location is too far from ${
                    selectedAreaObj?.name || "the area"
                  }`
                : `Location is outside ${selectedAreaObj?.name || "the area"}`
            : language === "arabic"
              ? "حفظ"
              : "Save"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Redux ---------------- */

const mapStateToProps = (state) => ({
  language: state.store.language,
  themeColor: state.store.store.settings.dark || "#0099cc",
  settings: state.store.store.settings,
});

export default connect(mapStateToProps)(MapPicker);
